import {ReactNode} from 'react';

type Props = {
  approval: ReactNode;
  inspection: ReactNode;
  replacement: ReactNode;
  settlement: ReactNode;
  compatibility: ReactNode;
  summary: string;
  pendingChanges: boolean;
  disabled: boolean;
};

export function ReviewProcessingWorkflow({approval, inspection, replacement, settlement, compatibility, summary, pendingChanges, disabled}: Props) {
  const stepHeading = (number: number, id: string, title: string, description: string) => (
    <div className="mb-4 flex items-start gap-3">
      <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-semibold text-blue-700">{number}</span>
      <div><h3 id={id} className="text-base font-semibold text-gray-900">{title}</h3><p className="mt-1 text-xs leading-5 text-gray-500">{description}</p></div>
    </div>
  );
  return (
    <section aria-label="Review & processing" className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
      <h2 className="text-lg font-semibold text-gray-900">Review &amp; processing</h2>
      <p className="mt-1 text-sm text-gray-500">Choose who approves requests, which returned items need inspection, and when each outcome can proceed.</p>
      <section aria-labelledby="request-approval-title" className="mt-5 border-t border-gray-100 pt-5">
        {stepHeading(1, 'request-approval-title', 'Request approval', 'Approval accepts the request. It does not release a replacement or issue funds.')}
        {approval}
      </section>
      <section aria-labelledby="item-inspection-title" className="mt-5 border-t border-gray-100 pt-5">
        {stepHeading(2, 'item-inspection-title', 'Item inspection', 'A physical check after the returned items arrive. Inspection approval is separate from request approval.')}
        {inspection}
      </section>
      <section aria-labelledby="outcome-processing-title" className="mt-5 border-t border-gray-100 pt-5">
        {stepHeading(3, 'outcome-processing-title', 'Process each outcome', 'Choose the earliest event for each outcome. Required approval and inspection checks must also pass.')}
        <section aria-labelledby="exchange-processing-title" className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
          <h4 id="exchange-processing-title" className="text-sm font-semibold text-gray-900">Exchange replacement</h4>
          {replacement}
        </section>
        {compatibility}
        <div className="mt-4 space-y-4">{settlement}</div>
      </section>
      <section aria-label="Replacement workflow" className="mt-5 rounded-lg bg-blue-50 px-4 py-3">
        <h3 className="text-xs font-semibold text-blue-900">{pendingChanges ? 'Replacement workflow after saving' : 'Replacement workflow'}</h3>
        <p className="mt-1 text-sm leading-6 text-blue-900">{summary}</p>
      </section>
      <p className="mt-3 text-xs leading-5 text-gray-500">Submitted exchanges keep the processing rules they were confirmed with. Required approval, inspection, current inventory, any additional payment and cheaper-exchange refund checks must also be satisfied.</p>
      </fieldset>
    </section>
  );
}
